import { beginLoading, endLoading } from '@app/AppLayout/LoadingContext';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DataLoadErrorRow } from '@app/components/DataLoadError';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { RemoveButton } from '@app/components/RemoveButton';
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';
import { useEntity } from '@app/hooks/useEntity';
import { useFilterRestore } from '@app/hooks/useFilterRestore';
import { useRowRestore } from '@app/hooks/useRowRestore';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { ClientExecutive } from '@app/model/ClientExecutive';
import { useClientExecutiveStore } from '@app/store/entityStore';
import {
  ActionList,
  ActionListItem,
  AlertVariant,
  Button,
  Icon,
  PageSection,
  Pagination,
  PaginationVariant,
  SearchInput,
  Split,
  SplitItem,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip
} from '@patternfly/react-core';
import { PencilAltIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

const ClientExecutiveOverview: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  // '/clientexecutives' would otherwise read as "Clientexecutives" in the breadcrumb
  useBreadcrumbLabel('Client Executives');

  const { items: rows, error, retry } = useEntity(useClientExecutiveStore);
  const remove = useClientExecutiveStore(state => state.delete);

  // The search last typed here, kept only for the trip out to a client executive and back.
  const { restored, keep } = useFilterRestore<{ filter: string }>('client-executives');
  const [filter, setFilter] = React.useState<string>(restored?.filter ?? '');
  const { restoredPage, rememberRow, rowProps } = useRowRestore('client-executives');
  const [page, setPage] = React.useState<number>(restoredPage ?? 1);
  const { perPage, setPerPage } = useTablePreferences('client-executives', 10);

  React.useEffect(() => { keep({ filter }); }, [filter, keep]);

  // --- Derived State: Filtering & Pagination ---
  const filteredRows = React.useMemo<ClientExecutive[]>(() => {
    if (!filter.trim()) return rows;
    const lowerFilter = filter.toLowerCase();
    return rows.filter((ce: ClientExecutive) => ce.name.toLowerCase().includes(lowerFilter));
  }, [rows, filter]);

  const paginatedRows = React.useMemo<ClientExecutive[]>(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  const onSearch = (_event: any, value: string) => {
    setFilter(value);
    setPage(1);
  };

  const handleSetPage = (_evt: any, newPage: number) => {
    setPage(newPage);
  };

  const handlePerPageSelect = (_evt: any, newPerPage: number, newPage: number) => {
    setPage(newPage);
    setPerPage(newPerPage);
  };

  const onRemoveConfirm = async (_event: any, ce: ClientExecutive) => {
    beginLoading();
    try {
      await remove(ce.id);
      addNotification(`Client Executive "${ce.name}" removed successfully`, AlertVariant.success);
    } catch (error: any) {
      addNotification('Error removing client executive', AlertVariant.danger, error.message);
    } finally {
      endLoading();
    }
  };

  const renderPagination = (variant: string, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact}
      itemCount={filteredRows.length}
      page={page}
      perPage={perPage}
      onSetPage={handleSetPage}
      onPerPageSelect={handlePerPageSelect}
      perPageOptions={[{ title: '10', value: 10 }, { title: '20', value: 20 }, { title: '50', value: 50 }]}
      variant={variant as PaginationVariant}
      titles={{ paginationAriaLabel: `${variant} pagination` }}
    />
  );

  return (
    <React.Fragment>
      <PageSection isWidthLimited>
        <Split hasGutter>
          <SplitItem><Title headingLevel="h1" size="4xl">Client Executives</Title></SplitItem>
          <SplitItem isFilled />
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <Button variant="primary" id="new-button" onClick={() => navigate('/clientexecutives/new')}>New Client Executive</Button>
              </ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      <PageSection isWidthLimited isFilled>
        <Toolbar id="compact-toolbar">
          <ToolbarContent>
            <ToolbarGroup>
              <ToolbarItem>
                <SearchInput
                  aria-label="name filter"
                  placeholder="Filter by name..."
                  value={filter}
                  onChange={onSearch}
                  onClear={(ev) => onSearch(ev, '')}
                  onSearch={onSearch}
                />
              </ToolbarItem>
            </ToolbarGroup>
            <ToolbarItem variant="pagination">{renderPagination('top', true)}</ToolbarItem>
          </ToolbarContent>
        </Toolbar>
        {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
            gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
        <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset))', minHeight: 0 }}>
          <InnerScrollContainer>
            <Table aria-label="Client Executives Table" isStickyHeader variant='compact' gridBreakPoint="">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Regions</Th>
                  <Th>Email</Th>
                  <Th>Mobile</Th>
                  <Th isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedRows.map((row) => (
                  <Tr key={row.id} {...rowProps(row.id)}>
                    <Td dataLabel="Name">{row.name}</Td>
                    {/* Maps the regions array into a comma-separated list */}
                    <Td dataLabel="Regions">
                      {row.regions && row.regions.length > 0
                        ? row.regions.map(r => r.name).join(', ')
                        : 'N/A'}
                    </Td>
                    <Td dataLabel="Email">{row.email}</Td>
                    <Td dataLabel="Mobile">{row.mobile}</Td>
                    <Td dataLabel="Actions" modifier="nowrap"
                      isStickyColumn stickyMinWidth="120px" stickyRightOffset="0" hasLeftBorder>
                      <ActionList>
                        <ActionListItem>
                          <Tooltip content="Edit">
                            <Button
                              icon={<Icon size='lg'><PencilAltIcon /></Icon>}
                              variant="plain"
                              id="edit-button"
                              onClick={() => { rememberRow(row.id, page); navigate(`/clientexecutives/${row.id}`); }}
                            />
                          </Tooltip>
                        </ActionListItem>
                        <ActionListItem>
                          <RemoveButton
                            confirmationMessage={`Confirm client executive "${row.name}" removal?`}
                            onRemoveConfirm={(ev) => onRemoveConfirm(ev, row)}
                          />
                        </ActionListItem>
                      </ActionList>
                    </Td>
                  </Tr>
                ))}
                {/* Nothing to show because the load failed, rather than because the list is empty */}
                {error && paginatedRows.length === 0 && (
                  <DataLoadErrorRow colSpan={5} error={error} entityLabel="client executives" onRetry={retry} />
                )}
              </Tbody>
            </Table>
          </InnerScrollContainer>
        </OuterScrollContainer>
        {renderPagination('bottom', false)}
      </PageSection>
    </React.Fragment>
  );
};

export { ClientExecutiveOverview };
